import CoverImageModal from '@/components/CoverImageModal';
import CreateEditCategoryModal from '@/components/CreateEditCategoryModal';
import DeleteArchiveCategoryModal from '@/components/DialogModal';
import DynamicSegmentedButton from '@/components/DynamicSegmentedButton';
import ProjectSelector from '@/components/ProjectSelector/ProjectSelector';
import ThreeDotsCMButton from '@/components/ThreeDotsCMButton';
import { CATEGORIES_ICONS } from '@/constants/category';
import { CronofySyncEventPayload } from '@/gql/cronofy/types';
import { keys } from '@/gql/global/keys';
import { ARCHIVE_PROJECT, RESTORE_PROJECT } from '@/gql/project';
import { refetchQueriesForArchive } from '@/pages/CategoryManager';
import { RoutesList } from '@/routes/routesList';
import { cleanCategoryForMutation } from '@/services/categories';
import { useCategories, useDeleteCategory, useUpdateCategoryMutation } from '@/services/categories/hooks';
import { useCronofySyncEventMany } from '@/services/cronofy/hooks';
import { fetchData } from '@/services/graphql';
import { queryClient } from '@/services/graphql/queryClient';
import { getWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useProjects } from '@/services/project/hooks';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { IconChevronLeft, IconMountain } from '@/theme/icons';
import { Category } from '@/types/category';
import { ImageInfo } from '@/types/inspirations';
import { refetchCategoryAndBlocks } from '@/utils/category';
import { getColorFromToken } from '@/utils/color';
import { fixUncategorizedName, isUncategorized } from '@/utils/index';
import rem from '@/utils/rem';
import { invalidateQueries, refetchQueries } from '@/utils/tanStackQuery';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from 'src/layouts/Dashboard';

import InnerSections from './InnerSections';
import { ACTIONS_AND_BLOCKS, MAGNIFICENT_7, useTranslatedOptions } from './constants';

const CategoryManagerDetails = () => {
  const { t } = useTranslation('CategoryManagerDetails');
  const OPTIONS = useTranslatedOptions();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const { data: categoriesList, isLoading: isCategoriesListLoading } = useCategories();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState<boolean>(false);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

  const updateCategory = useUpdateCategoryMutation();
  const { mutate: cronofySyncEventMany } = useCronofySyncEventMany();

  const handleToggle = (projectId: string) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId],
    );
  };

  const { data: projects } = useProjects({
    refetchOnMount: true,
  });

  const {
    isOpen: isEditCategoryOpen,
    onOpen: onEditCategoryOpen,
    onClose: onEditCategoryClose,
  } = useDisclosure({ id: 'editCategory' });

  const {
    isOpen: isImagesModalOpen,
    onOpen: onOpenImagesModal,
    onClose: onCloseImagesModal,
  } = useDisclosure({ id: 'unsplashModal' });

  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);
  const weeklyPlanId = getWeeklyPlanId(selectedDate);

  const categoryInfo = useMemo(
    () => categoriesList?.category?.find((el: Category) => el?.id === id),
    [categoriesList?.category, id],
  );

  const filteredProjects = useMemo(() => {
    return projects?.project.filter((project) => project.categoryId === categoryInfo?.id);
  }, [projects, categoryInfo?.id]);

  const uncategorized = useMemo(
    () => categoriesList?.category.find((c) => c.name.toLowerCase() === 'uncategorized'),
    [categoriesList?.category],
  );

  const checkingUncategorized = isUncategorized(categoryInfo) ? ACTIONS_AND_BLOCKS : MAGNIFICENT_7;
  const UPDATED_OPTIONS = isUncategorized(categoryInfo) ? [null, OPTIONS[1]] : OPTIONS;
  const [pageIndex, setPageIndex] = useState(checkingUncategorized);

  const isMobileScreen = useBreakpointValue({ base: true, lg: false });

  const uncategorizedFields = useMemo(() => {
    if (!uncategorized) {
      return {};
    }

    return {
      categoryId: uncategorized.id,
      category: {
        color: uncategorized.color,
        order: uncategorized.order,
        icon: uncategorized.icon,
        id: uncategorized.id,
        name: uncategorized.name,
      },
      categoryOrder: null,
    };
  }, [uncategorized]);

  const onGoBack = useCallback(() => {
    navigate(RoutesList.CategoryManagerPage);
  }, [navigate]);

  const deleteCategory = useDeleteCategory({
    onSuccess: (_, variables) => {
      if (!uncategorizedFields) {
        return;
      }

      refetchCategoryAndBlocks();

      invalidateQueries([keys.project.all.queryKey, keys.categoryEvents.list._def]);

      toast({
        title: t('toasts.categoryDeleted'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onGoBack();
    },
  });

  const onDeleteCategory = useCallback(async () => {
    if (!id) return;

    deleteCategory.mutateAsync({
      categoryId: id,
    });
  }, [deleteCategory, id]);

  const archiveActiveCategory = useCallback(async () => {
    if (!categoryInfo) return;

    updateCategory.mutate(
      {
        ...cleanCategoryForMutation(categoryInfo),
        isArchived: categoryInfo.isArchived ? false : true,
      },
      {
        onSuccess: async (data) => {
          setOpenArchiveModal(false);

          const isArchive = data.updateCategoryByPk.isArchived;

          if (filteredProjects && filteredProjects.length > 0) {
            if (isArchive) {
              const projectsPayload = {
                categoryId: data.updateCategoryByPk.id,
                isArchived: isArchive,
              };

              await fetchData<{ isArchived: boolean; categoryId: string }>(ARCHIVE_PROJECT, projectsPayload);
            } else {
              const projectsPayload = {
                ids: selectedProjects,
                isArchived: isArchive,
              };
              await fetchData<{ isArchived: boolean; ids: [string] }>(RESTORE_PROJECT, projectsPayload);
            }
          }

          if (data.updateCategoryByPk.actions) {
            const eventsToSync: CronofySyncEventPayload[] = data.updateCategoryByPk.actions
              .filter((action) => !action.isCanceled && action.scheduledDate && action.scheduledTime)
              .map((action) => ({
                eventId: action.id,
                eventType: 'Action',
                deleted: isArchive,
              }));

            if (eventsToSync.length) {
              cronofySyncEventMany({ events: eventsToSync });
            }
          }

          refetchQueries([keys.project.category(data.updateCategoryByPk.id)]);
          refetchQueriesForArchive();

          toast({
            title: t(`toasts.category${isArchive ? 'Archived' : 'Restored'}`),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        },
      },
    );

    setSelectedProjects([]);
  }, [updateCategory]);

  const onInsertCategoryImage = useCallback(
    (selectedImage: ImageInfo) => {
      if (!selectedImage?.imageUrl || !categoryInfo) {
        return;
      }

      updateCategory.mutate(
        {
          ...cleanCategoryForMutation(categoryInfo),
          selectedImageUrl: selectedImage?.imageUrl ?? '',
        },
        {
          onSuccess: () => {
            onCloseImagesModal();
            setIsSavingImage(false);
            refetchCategoryAndBlocks();

            toast({
              title: t('toasts.categoryImageSaved'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          },

          onError: (error) => {
            setIsSavingImage(false);

            toast({
              title: error?.response?.errors?.[0]?.message,
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          },
        },
      );
    },
    [categoryInfo, onCloseImagesModal, toast, updateCategory],
  );

  useEffect(
    () => () => {
      queryClient.removeQueries(keys.actions.all._ctx.week(weeklyPlanId ?? '').queryKey);
    },
    [weeklyPlanId],
  );

  useEffect(() => {
    if (uncategorized !== undefined) {
      setPageIndex(checkingUncategorized);
    }
  }, [checkingUncategorized, uncategorized]);

  return (
    <DashboardLayout
      paddingX={0}
      paddingTop={0}
      pageTitle={t('pageTitle')}
      editActionModalTooltip={t('tooltips.editActionModalTooltip')}
    >
      <Flex
        position="sticky"
        zIndex="banner"
        top="0"
        alignItems="center"
        flexDirection={{ base: 'column', lg: 'row' }}
        gap={{ base: rem(10), lg: rem(24) }}
        width="full"
        height={{ lg: rem(44) }}
        padding={`${rem(6)} ${rem(16)}`}
        boxShadow="0px 3px 10px 0px rgba(0,0,0,0.3)"
        backgroundColor="background-tertiary"
      >
        <HStack gap={rem(12)} width={{ base: 'full', lg: 'fit-content' }} height="full">
          <HStack height="full">
            <Button
              flexDirection="row"
              gap={rem(12)}
              display="flex"
              marginRight={rem(12)}
              onClick={onGoBack}
              variant="unstyled"
            >
              <Icon as={IconChevronLeft} boxSize={rem(14)} />
              <Text textStyle="large">{t('navigation.backToCategories')}</Text>
            </Button>

            <Divider color="stroke-primary" orientation="vertical" />
          </HStack>

          {categoryInfo && (
            <>
              <HStack gap={rem(12)} height="full" marginLeft={{ base: 'auto', lg: 'unset' }}>
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  width={rem(24)}
                  minWidth={rem(24)}
                  height={rem(24)}
                  minHeight={rem(24)}
                  borderRadius="full"
                  backgroundColor={categoryInfo.color}
                >
                  <Icon
                    as={CATEGORIES_ICONS[categoryInfo.icon ?? 'uncategorized']}
                    width={rem(11)}
                    height={rem(11)}
                    color={getColorFromToken(categoryInfo.color, undefined, '800')}
                  />
                </Flex>
                <Text textStyle="h5BarSB" textTransform="uppercase" noOfLines={1}>
                  {fixUncategorizedName(categoryInfo.name)}
                </Text>

                {isMobileScreen && (
                  <ThreeDotsCMButton
                    onEdit={onEditCategoryOpen}
                    onDelete={() => setOpenDeleteModal(true)}
                    isUncategorized={isUncategorized(categoryInfo)}
                  />
                )}
              </HStack>
            </>
          )}
        </HStack>

        {categoryInfo && (
          <HStack flexGrow={1} width={{ base: 'full', lg: 'unset' }}>
            <Box marginRight="auto">
              <DynamicSegmentedButton
                onChange={setPageIndex}
                options={UPDATED_OPTIONS}
                startIndex={isUncategorized(categoryInfo) ? 1 : pageIndex}
                disabled={isUncategorized(categoryInfo)}
                layoutId="CategoryManagerDetails"
                buttonPaddingX={{ base: rem(4), sm: rem(10) }}
              />
            </Box>

            <HStack height="full" marginLeft="auto">
              {pageIndex === MAGNIFICENT_7 && !isUncategorized(categoryInfo) && (
                <Button height="full" onClick={onOpenImagesModal} paddingInline={rem(8)} size="sm" variant="secondary">
                  <HStack alignItems="center">
                    <Icon as={IconMountain} boxSize={rem(14)} color="gray.300" />
                    <Text textStyle="small" color="text-primary" fontWeight={900}>
                      {!isMobileScreen && `${t('buttons.changeCoverImage').split(' ')[0]} `}
                      {t('buttons.coverImage')}
                    </Text>
                  </HStack>
                </Button>
              )}

              {!isMobileScreen && (
                <ThreeDotsCMButton
                  onEdit={onEditCategoryOpen}
                  onDelete={() => setOpenDeleteModal(true)}
                  onArchive={() => setOpenArchiveModal(true)}
                  isUncategorized={isUncategorized(categoryInfo)}
                  isArchived={categoryInfo.isArchived}
                />
              )}
            </HStack>
          </HStack>
        )}
      </Flex>

      {categoryInfo && <InnerSections pageIndex={pageIndex} isLoading={isCategoriesListLoading} data={categoryInfo} />}

      {!categoryInfo && !isCategoriesListLoading && (
        <Flex alignItems="center" justifyContent="center" flexDirection="column" gap={5} marginTop={rem(128)}>
          {t('errorMessages.categoryNotFound')}
          <Button
            width="fit-content"
            height="full"
            onClick={() => navigate(RoutesList.CategoryManagerPage)}
            paddingInline={rem(8)}
            paddingY={rem(5)}
            size="sm"
            variant="secondary"
          >
            <Text textStyle="small">{t('buttons.categoriesList')}</Text>
          </Button>
        </Flex>
      )}

      <CreateEditCategoryModal
        category={categoryInfo}
        isOpen={isEditCategoryOpen}
        onClose={onEditCategoryClose}
        isEditMode
      />

      {openDeleteModal && (
        <DeleteArchiveCategoryModal
          isOpen={openDeleteModal}
          title={t('modals.deleteCategory.title')}
          message={t('modals.deleteCategory.message')}
          confirmText={t('modals.deleteCategory.confirmText')}
          cancelText={t('modals.deleteCategory.cancelText')}
          confirmVariant="danger"
          onCancel={() => setOpenDeleteModal(false)}
          onConfirm={onDeleteCategory}
        />
      )}

      {openArchiveModal && (
        <DeleteArchiveCategoryModal
          isOpen={openArchiveModal}
          title={t(`modals.archiveRestoreCategory.titles.${categoryInfo?.isArchived ? 'restore' : 'archive'}`)}
          message={
            categoryInfo?.isArchived && filteredProjects && filteredProjects?.length > 0
              ? t('modalMessages.categoryRestoreConfirmation')
              : categoryInfo?.isArchived
                ? t('modalMessages.categoryWithoutProjectsConfirmation')
                : t('modalMessages.categoryArchiveConfirmation')
          }
          confirmText={t(
            `modals.archiveRestoreCategory.confirmTexts.${categoryInfo?.isArchived ? 'restore' : 'archive'}`,
          )}
          cancelText={t('modals.archiveRestoreCategory.cancelText')}
          confirmVariant="primary"
          onCancel={() => {
            setOpenArchiveModal(false);
          }}
          onConfirm={archiveActiveCategory}
        >
          {categoryInfo?.isArchived && filteredProjects && filteredProjects.length > 0 ? (
            <ProjectSelector onToggle={handleToggle} projects={filteredProjects} selectedProjects={selectedProjects} />
          ) : undefined}
        </DeleteArchiveCategoryModal>
      )}

      <CoverImageModal
        isImagesModalOpen={isImagesModalOpen}
        onCloseImagesModal={onCloseImagesModal}
        onInsertInspirationImage={onInsertCategoryImage}
        isSavingImage={isSavingImage}
        setIsSavingImage={setIsSavingImage}
      />
    </DashboardLayout>
  );
};

export default CategoryManagerDetails;
